<template>
  <v-container>
    <v-row>
      <project-summary v-bind:isBalance="false"></project-summary>
    </v-row>
    <v-row>
      <v-col>
        <v-text-field
          class="search-text"
          v-model="search"
          hide-details
          :label="$t('search.text')"
          :append-icon="this.search.length == 0 ? 'mdi-magnify' : 'mdi-close'"
          @click:append="search = ''"
        />
      </v-col>
    </v-row>
    <v-row class="v-row-group__header">
      <v-col cols="10">
        <v-card flat>
          <v-data-table
            class="data-table__project"
            id="animalOutputDataTable"
            ref="animalOutputDataTable"
            :headers="headers"
            height="70vh"
            fixed-header
            :items="this.project.animal_output"
            :sort-by="this.nameProperty"
            default-sort="false"
            :options.sync="options"
            @click:row="itemDetails"
            hide-default-footer
            :item-key="`name_${this.$store.locale}`"
            group-by="category_id"
            :single-expand="false"
            single-select
            :search="search"
          >
            <!-- Group Headers -->
            <template v-slot:group.header="{ group, items, isOpen, toggle }">
              <td class="icon-cell">
                <v-btn
                  @click="toggle"
                  icon
                  :ref="group"
                  v-ripple="false"
                  class="data-table__toggle-btn"
                >
                  <v-icon v-if="isOpen">
                    mdi-chevron-down
                  </v-icon>
                  <v-icon v-else>
                    mdi-chevron-right
                  </v-icon>
                </v-btn>
              </td>
              <td class="text-left">
                {{ getCategoryName(group) }}
              </td>
              <td>
                <span>{{ getCategoryAmount(items) }}</span>
              </td>
              <td></td>
              <td v-if="items[0].category_id !== 'animal_07'" class=".input-cell">
                <v-tooltip top 
                  open-delay="800">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon
                      v-bind="attrs"
                      v-on="on"
                      >
                      <v-icon
                        medium
                        class="data-table__add-output-btn"
                        @click="addNewAnimalOutputToggle({ category_id: group }, toggle, isOpen)"
                      >
                        mdi-plus
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('animalProduction.dataTable.tooltipAddToCategory') }}</span>
                </v-tooltip>
              </td>
            </template>

            <!-- Table Body -->

            <template v-slot:item="val">
              <v-lazy :value="val.isExpanded" 
              tag="tr"
              transition=""
              >
                <td></td>
                <td class="text-left">
                  <v-tooltip v-if="hasDescription(val.item)"
                    bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="val.item['name_'+$i18n.locale] ? val.item['name_'+$i18n.locale] : getItemName(val.item)"
                        :readonly="val.item.id != undefined"
                        @blur="setOutputName(val.item, $event.target.value)"
                        hide-details
                        v-bind="attrs"
                        v-on="on"
                        @click="itemDetails(val.item)"
                      />
                    </template>
                    <span>{{val.item['description_'+$i18n.locale]}}</span>
                  </v-tooltip>
                  <v-text-field v-else
                    :value="val.item['name_'+$i18n.locale] ? val.item['name_'+$i18n.locale] : getItemName(val.item)"
                    :readonly="val.item.id != undefined"
                    @blur="setOutputName(val.item, $event.target.value)"
                    hide-details
                    :autofocus="focusOnEditButton(val.item)"
                    :key="val.item.added_uid"
                    @click="itemDetails(val.item)"
                  />
                </td>
                <td class="text-right">
                  <v-text-field
                    v-model.number="val.item.amount_input"
                    @keypress="preventUndesiredInput"
                    @keyup="handlerAmount(val.item.amount_input, val.item)"
                    @wheel="$event.currentTarget.blur()"
                    v-on:input="setAnimalValuesAmount(val.item, $event)"
                    size="6"
                    hide-details
                    type="number"
                    @click="itemDetails(val.item)"
                  />
                </td> 
                <td class="text-right">
                  <span @click="itemDetails(val.item)">{{getUnitName(val.item)}}</span>
                </td> 
                <td>
                  <v-menu v-if="val.item.category_id !== 'animal_07'" bottom left>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on">
                        <v-icon medium color="grey">mdi-dots-horizontal </v-icon>
                      </v-btn>
                    </template>
                    <v-list v-if="!val.item.id">
                      <v-list-item
                        v-for="(elem, i) in context_menu"
                        :key="i"
                        @click="elem.action(val.item)"
                      >
                        <v-list-item-title>{{ elem.title }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                    <v-list v-else>
                      <v-list-item @click="addNewAnimalOutput(val.item)">
                        <v-list-item-title>{{ $t('contextMenu.duplicate') }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu> 
                </td>
              </v-lazy>
            </template> 
          </v-data-table>
        </v-card>
      </v-col>

      <v-col cols="2">
        <v-card class="data-table__details-view animal-details" tile>
          <td v-if="selectedId !== -1">
            <span class="data-table__details-view-headline">{{ selected_output['name_'+this.$i18n.locale] }}</span>
            <p></p>
            <p v-if="selected_output" font-size="small">
              <span class="data-table__details-view-sub-headline">
                {{ $t('animalProduction.detailsView.nutrientAccumulation') }}
              </span>
            </p>
            <p></p>
            <p>
              <v-text-field v-if="selected_output.category_id !== 'animal_07'"
                :readonly="selected_output.id != undefined"
                v-model="selected_output.N"
                prefix="N:"
                :suffix="$t('animalProduction.detailsView.suffix1')"
                @keypress="preventUndesiredInput"
                v-on:input="setN(selected_output, $event)"
                type="number"
              >
              </v-text-field>

               <v-text-field v-else
                :readonly="selected_output.id != undefined"
                :value="selected_output.N * 0.001"
                prefix="N:"
                :suffix="$t('animalProduction.detailsView.suffix2')"
                @keypress="preventUndesiredInput"
                v-on:input="setN(selected_output, $event)"
                type="number"
              >
              </v-text-field>
            </p>
            <p>
              <v-text-field v-if="selected_output.category_id !== 'animal_07'"
                :readonly="selected_output.id != undefined"
                v-model="selected_output.P"
                prefix="P:"
                :suffix="$t('animalProduction.detailsView.suffix1')"
                @keypress="preventUndesiredInput"
                v-on:input="setP(selected_output, $event)"
                type="number"
              >
              </v-text-field>

              <v-text-field v-else
                :readonly="selected_output.id != undefined"
                :value="selected_output.P * 0.001"
                prefix="P:"
                :suffix="$t('animalProduction.detailsView.suffix2')"
                @keypress="preventUndesiredInput"
                v-on:input="setP(selected_output, $event)"
                type="number"
              >
              </v-text-field>
            </p>
            <p>
              <v-text-field v-if="selected_output.category_id !== 'animal_07'"
                :readonly="selected_output.id != undefined"
                v-model="selected_output.K"
                prefix="K:"
                :suffix="$t('animalProduction.detailsView.suffix1')"
                @keypress="preventUndesiredInput"
                v-on:input="setK(selected_output, $event)"
                type="number"
              >
              </v-text-field>

               <v-text-field v-else
                :readonly="selected_output.id != undefined"
                :value="selected_output.K * 0.001"
                prefix="K:"
                :suffix="$t('animalProduction.detailsView.suffix2')"
                @keypress="preventUndesiredInput"
                v-on:input="setK(selected_output, $event)"
                type="number"
              >
              </v-text-field>
            </p>
          </td>
          <td v-else>
           <span class="data-table__details-view-headline">{{ $t('animalProduction.detailsView.title') }}</span>
            <p></p>
            <p>
              <span class="data-table__details-view-sub-headline">
              {{ $t('animalProduction.detailsView.subtext') }}
              </span>
            </p>
            <p></p>
          </td>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import store from "@/store/index";
import ProjectSummary from '../components/ProjectSummary.vue';
import { nameCheck } from "@/main.js";

export default {
  components: { ProjectSummary },
  data() {
    return {
      context_menu: [
        {
          title: this.$i18n.t('contextMenu.remove'),
          action: function(item) {
            store.dispatch("removeAnimalOutput", item);
          },
        },
        {
          title: this.$i18n.t('contextMenu.duplicate'),
          action: function(item) {
            store.commit("addNewAnimalOutput", item);
          },
        },
      ],
      options: {
        itemsPerPage: -1,
      },
      headers: [
        {
          text: " ",
          sortable: false,
          width: 50
        },
        {
          text: this.$i18n.t('animalProduction.dataTable.animal'),
          sortable: false,
          value: "name_"+this.$i18n.locale,
          align: "left",
          width: 550
        },
        { text: this.$i18n.t('animalProduction.dataTable.amount'), value: "amount_input", align: "right", cellClass: "right-input", width: 150 },
        { text: this.$i18n.t('animalProduction.dataTable.unit'), value: "unit_id", align: "right", cellClass: "right-input", width: 50 },
        { text: " ", value: "actions", sortable: false, align: "right", width: 50 },
      ],
      selected_output: {},
      selectedRows: [],
      selectedId: -1,
      search: ''
    };
  },

  computed: {
    ...mapState(["project", "categories", "locale", "units"]),
    nameProperty: function () {
      return 'name_'+this.locale
    }    
  },

  mounted() {
    this.collapseTableGroups(this, "animalOutputDataTable");
  },

  methods: {
    hasDescription(item) {
      if (item['description_'+this.$i18n.locale] && item['description_'+this.$i18n.locale].length > 0) {
        return true;
      } else {
        return false;
      }
    },

    focusOnEditButton(item) {
      if (item.added_uid && item['name_'+this.$i18n.locale] === '') {
        return true
      } else {
        return false
      }
    },

    getUnitName(item) {
       let unitName = this.units.find((c) => c.id == item.unit_id);
       return unitName ? unitName['name_'+this.$i18n.locale] : "";
    },

    preventUndesiredInput($event) {
      var keyCode = ($event.keyCode ? $event.keyCode : $event.which);
      if (keyCode < 48 || keyCode > 57) {
        $event.preventDefault();
      }
    },

    setN(item, val) {
      if (val && Number(val)) {
        let index = this.project.animal_output.indexOf(item);
        this.$store.commit('updateAnimalOutputValueN', {index,val})
      }
    },

    setP(item, val) {
      if (val && Number(val)) {
        let index = this.project.animal_output.indexOf(item);
        this.$store.commit('updateAnimalOutputValueP', {index,val})
      }
    },

    setK(item, val) {
      if (val && Number(val)) {
        let index = this.project.animal_output.indexOf(item);
        this.$store.commit('updateAnimalOutputValueK', {index,val})
      }
    },

    getCategoryName(category) {
      let cat = this.categories.find((c) => c.id == category);
      let name = cat ? cat[this.nameProperty] : "";
      return name;
    },

    getItemName(item) {
      if (!item[this.nameProperty]) {
        return "";
      }
      return item[this.nameProperty];
    },

    itemDetails(item) {
      this.selected_output = item;
      this.selectedId = item['name_'+this.$i18n.locale];
    },

    setAnimalValuesAmount(item, val) {      
      if (val !== undefined) {
        let index = this.project.animal_output.indexOf(item);
        this.$store.commit('updateAnimalAmountValue', {index,val})
      }
    },

    setOutputName(item, newValue) {
      if (newValue === "") {
        let names = this.project.animal_output.map(out => out['name_'+this.$i18n.locale])
        let newName = this.getCategoryName(item.category_id)
        newName = nameCheck(newName, names)
        item['name_'+this.$i18n.locale] = newName;
      } else {
        item['name_'+this.$i18n.locale] = newValue;
      }
    },

    handlerAmount(val, item) {
      if (val === "") {
        item.amount_input = 0;
      }
    },

    getCategoryAmount(items) {
      return items
        .filter(item => item.amount_input)
        .reduce((val, item) => val + parseInt(item.amount_input), 0);
    },

    addNewAnimalOutputToggle(item, toggle, isOpen) {
      if(!isOpen) {toggle()}
      this.$store.commit("addNewAnimalOutput", item);
    },

     addNewAnimalOutput(item) {
      this.$store.commit("addNewAnimalOutput", item);
    },

    /**
     * For a v-data-table using grouping this function collapses
     * all groups except for the first one.
     * @param vue a VueComponent instance (here just 'this')
     * @param ref the 'ref' value of a v-data-table
     */
    collapseTableGroups(vue, ref, expanded=false) {
      // lookup the table instance
      let table = vue.$refs[ref];
      // the $refs array contains the group labels and the table's ref name
      let refs = Object.keys(vue.$refs);
      // by setting the value on the table's 'openCache' to true/false, the groups
      // are either expanded or collapsed
      // must use $set to have this reactive
      refs.forEach(function(element) {
        table.$set(table.openCache, element, expanded);
      });
    },

    rowClicked(row) {
      this.toggleSelection(row.id);
    },

    toggleSelection(keyID) {
      if (this.selectedRows.includes(keyID)) {
        this.selectedRows = this.selectedRows.filter(
          (selectedKeyID) => selectedKeyID !== keyID
        );
      } else {
        this.selectedRows.push(keyID);
      }
    },
  },
  
  watch: {
    search() {
      this.collapseTableGroups(this, "dataTable", this.search.length > 0);
    }
  }
};
</script>
